import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getTreatment } from "@/lib/api";
import { boldify } from "@/lib/richtext";

const IMG_BY_SLUG = {
  "studio-a-ritual": "/images/studio-room.jpg",
  "deep-body-ritual": "/images/editorial/treatment-hands.jpg",
};

export default function TreatmentDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);

  useEffect(() => {
    setT(null);
    getTreatment(slug).then(setT).catch(() => setT({ notfound: true }));
  }, [slug]);

  if (!t) return <div className="py-32 text-center text-[#55524C]">Loading…</div>;
  if (t.notfound) return (
    <div className="py-32 text-center">
      <p className="serif text-3xl">Treatment not found</p>
      <Link to="/treatments" className="link-underline mt-6 inline-block">Back to treatments</Link>
    </div>
  );

  return (
    <div data-testid="treatment-detail">
      <section className="pt-16 pb-8">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Link to="/treatments" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#55524C] hover:text-[#2E2B27] transition-colors">
            <ArrowLeft size={14} /> All treatments
          </Link>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7 img-zoom">
            <img src={IMG_BY_SLUG[t.slug] || "/images/studio-room.jpg"} alt={t.name} className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="label-eyebrow">{t.duration} · {t.price}</div>
            <h1 className="serif mt-5 text-[40px] sm:text-[56px] leading-[1] tracking-tight">{t.name}</h1>
            <p className="serif italic mt-3 text-[22px] text-[#55524C]">{t.subtitle || t.tagline}</p>
            <div className="mt-8 space-y-4 text-[16px] leading-relaxed text-[#2E2B27]">
              {t.description.split("\n\n").map((p, pi) => (
                <p key={pi}>{boldify(p)}</p>
              ))}
            </div>

            {t.experience_groups ? (
              t.experience_groups.map((g) => (
                <div className="mt-10" key={g.title}>
                  <div className="label-eyebrow">{g.title}</div>
                  <ul className="mt-4 space-y-2">
                    {g.items.map((e) => (
                      <li key={e} className="text-[15px] text-[#2E2B27] border-b border-[#D5CDBF] py-3">{e}</li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <div className="mt-10">
                <div className="label-eyebrow">What Is Included</div>
                <ul className="mt-4 space-y-2">
                  {t.experience.map((e) => (
                    <li key={e} className="text-[15px] text-[#2E2B27] border-b border-[#D5CDBF] py-3">{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {t.for_you && (
              <div className="mt-10">
                <div className="label-eyebrow">This Ritual May Be For You If</div>
                <ul className="mt-4 space-y-2">
                  {t.for_you.map((o) => (
                    <li key={o} className="text-[15px] text-[#55524C] border-b border-[#E7E2D6] py-3">{o}</li>
                  ))}
                </ul>
              </div>
            )}

            {t.outcomes && (
              <div className="mt-10">
                <div className="label-eyebrow">You May Leave Feeling</div>
                <ul className="mt-4 space-y-2">
                  {t.outcomes.map((o) => (
                    <li key={o} className="text-[15px] text-[#55524C] border-b border-[#E7E2D6] py-3">{o}</li>
                  ))}
                </ul>
              </div>
            )}

            <Link to="/book" data-testid="detail-book-btn" className="btn-primary mt-10 inline-flex">
              Begin Your Ritual
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
